import {
  BaseImageUpdate,
  getIssuesCountMapOrderedSeverity,
  Severity,
  VulnerabilitiesBySeverity,
  Vulnerability
} from "@/domain/vulnerability"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { getLastDetectedCellData } from "@/helpers/table.helpers"
import { IMAGE_DRAWER_ID } from "@/organisms/Drawers/const"
import { getApplicationsMetaData } from "@/pages/TriagePage/application/useTriage.selector"

import type {
  BaseImageUpdateVulnerability,
  ContainerBaseImageDetailsResponse,
  ContainerBaseImageResponse
} from "@/api/useInventory.types"

export const getContainerBaseImagesTabData = (
  response: ContainerBaseImageResponse
) => {
  const { data = [], metadata } = response || {}

  return {
    metadata,
    data: data.map((record) => {
      const { data: dataRecord, id, displayName } = record || {}
      const { applications, issuesSummary } = dataRecord
      const imagesNames = dataRecord.imagesNames || []
      const relatedImages = dataRecord.relatedImages || []
      const [title = "", tag = ""] = imagesNames[0]?.split?.(":") || []
      const issuesCounts = getIssuesCountMapOrderedSeverity(issuesSummary)

      return {
        key: id,
        title,
        tag,
        displayName,
        applicationsCount: getApplicationsMetaData(applications),
        sbomQuery: `baseImageName=${imagesNames.length ? imagesNames.join(",") : displayName}`,
        imageName: {
          copyable: true,
          count: imagesNames.length - 1,
          items: imagesNames,
          label: imagesNames[0] || "",
          width: "100%"
        },
        relatedImage: {
          copyable: true,
          count: relatedImages.length - 1,
          items: relatedImages.map((img) => ({
            name: img.displayName,
            drawerEntityId: img.id,
            drawerId: IMAGE_DRAWER_ID
          })),
          label: relatedImages[0]?.displayName || "",
          width: "100%"
        },
        ...dataRecord,
        digestShort: dataRecord.digest?.substring(7, 19),
        firstSeen: getDateInUserLocale(
          getDateFromSeconds(Number(dataRecord.firstSeen)),
          DEFAULT_LONG_FORMAT
        ),
        severity: issuesCounts,
        ...getLastDetectedCellData(record)
      }
    })
  }
}

export const getInventoryContainerBaseImageDetails = (
  response: ContainerBaseImageDetailsResponse,
  t: (key: string) => string,
  { minorUpdate, majorUpdate }: { minorUpdate?: string; majorUpdate?: string }
) => {
  const { data } = response || {}
  const { minorUpdateVulnerabilities, majorUpdateVulnerabilities } = data || {}
  const minorUpdateVulns = convertBaseImageVulnsToVulnsBySeverity(
    minorUpdateVulnerabilities
  )

  const minorUpdateImage: BaseImageUpdate = {
    vulnerabilities: minorUpdateVulns,
    type: t("inventory.baseImageDrawer.minorUpgrade"),
    version: minorUpdate || ""
  }

  const majorUpdateVulns = convertBaseImageVulnsToVulnsBySeverity(
    majorUpdateVulnerabilities
  )

  const majorUpdateImage: BaseImageUpdate = {
    vulnerabilities: majorUpdateVulns,
    type: t("inventory.baseImageDrawer.majorUpgrade"),
    version: majorUpdate || ""
  }

  const updates = [minorUpdateImage, majorUpdateImage].filter(
    (image) => !!image.version
  )

  return {
    data: { updates }
  }
}

function convertBaseImageVulnsToVulnsBySeverity(
  response: BaseImageUpdateVulnerability[] | undefined
): VulnerabilitiesBySeverity | undefined {
  if (!response) {
    return undefined
  }

  const vulnerabilitiesBySeverity: VulnerabilitiesBySeverity = {}
  response?.forEach((vulnerability: BaseImageUpdateVulnerability) => {
    const severity = vulnerability.severity.toLowerCase() as Severity
    if (!vulnerabilitiesBySeverity[severity]) {
      vulnerabilitiesBySeverity[severity] = [] as Vulnerability[]
    }

    const vulnerabilityObj: Vulnerability = {
      cve: vulnerability.id,
      cvss: -1,
      severity: severity
    }

    ;(vulnerabilitiesBySeverity[severity] as Vulnerability[]).push(
      vulnerabilityObj
    )
  })

  return vulnerabilitiesBySeverity
}
